import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import type { Column } from '@/entities/board'
import { type Employee, EmployeeChip } from '@/entities/employee'
import { myRunningOn, runningQueryOptions } from '@/entities/time-entry'
import { Button } from '@/shared/ui/button'
import { Dialog } from '@/shared/ui/dialog'
import { Icon } from '@/shared/ui/icon'
import { useReleaseTask, useTakeTask } from '../api/take'

interface TakeButtonProps {
  task: {
    id: string
    boardId: string
    columnId: string
    assigneeId?: string | null | undefined
    activeEmployeeId?: string | null | undefined
  }
  columns: Column[]
  meId: string | undefined
  // Кто есть кто — для подписей «ведёт …» и «назначена на …». Пусто → без имён.
  employees?: Employee[]
  // compact — иконка play/stop для карточки доски (hover-действие); диалоги те же.
  compact?: boolean
}

// Кнопка отражает МОЙ таймер, а не колонку: задача может стоять в «В работе» с
// чужим таймером или вовсе без него, и «Остановить» на ней было бы враньём.
export function TakeButton({
  task,
  columns,
  meId,
  employees = [],
  compact = false,
}: TakeButtonProps) {
  const running = useQuery(runningQueryOptions)
  const take = useTakeTask(task, columns, meId)
  const release = useReleaseTask(task)
  const [confirm, setConfirm] = useState<'busy' | 'takeover' | null>(null)

  const mine = myRunningOn(running.data, task.id)
  const others = (running.data ?? []).filter((r) => r.taskId !== task.id)
  const byId = new Map(employees.map((e) => [e.id, e]))
  const activeOther =
    task.activeEmployeeId && task.activeEmployeeId !== meId ? task.activeEmployeeId : null
  const assigneeOther = task.assigneeId && meId && task.assigneeId !== meId ? task.assigneeId : null

  // Свой таймер идёт → единственное осмысленное действие «отпустить»: интервал
  // закрывается, задача уезжает туда, откуда её взяли.
  if (mine) {
    return compact ? (
      <button
        type="button"
        aria-label="Отпустить"
        title="Отпустить — вернётся туда, откуда взяли"
        onClick={() => release.mutate()}
        disabled={release.isPending}
        // На таче мишень разгоняется отрицательным полем: иконка 14px пальцем не
        // берётся, а ряд от этого не раздвигается.
        className="rounded-control p-1 text-accent transition-colors hover:text-text-primary disabled:opacity-40 touch:-m-2.5 touch:p-3.5"
      >
        <Icon name="stop" size={14} />
      </button>
    ) : (
      <Button variant="secondary" onClick={() => release.mutate()} disabled={release.isPending}>
        <Icon name="stop" size={16} />
        Отпустить
      </Button>
    )
  }

  const start = (stopOthers: boolean) => take.mutate({ stopOthers })

  // Порядок вопросов: сначала «чья задача», потом «что с моими прежними» —
  // перехват меняет ответственность, это важнее параллельного тика.
  const onStart = () => {
    if (activeOther || assigneeOther) setConfirm('takeover')
    else if (others.length > 0) setConfirm('busy')
    else start(false)
  }

  const afterTakeover = () => {
    if (others.length > 0) {
      setConfirm('busy')
      return
    }
    setConfirm(null)
    start(false)
  }

  const label = activeOther ? 'Перехватить' : 'Взять в работу'
  const activeEmployee = activeOther ? byId.get(activeOther) : undefined

  return (
    <>
      {compact ? (
        <button
          type="button"
          aria-label={label}
          title={activeEmployee ? `Ведёт ${activeEmployee.name} — перехватить` : 'Взять в работу'}
          onClick={onStart}
          disabled={take.isPending}
          className="rounded-control p-1 text-text-tertiary transition-colors hover:text-accent disabled:opacity-40 touch:-m-2.5 touch:p-3.5"
        >
          <Icon name={activeOther ? 'reply' : 'play'} size={14} />
        </button>
      ) : (
        <div className="flex items-center gap-2">
          {activeOther && (
            <span className="inline-flex items-center gap-1.5 text-sm text-text-tertiary">
              <span className="h-2 w-2 rounded-pill bg-accent" aria-hidden />
              ведёт
              {activeEmployee ? (
                <EmployeeChip employee={activeEmployee} />
              ) : (
                <span className="text-text-secondary">коллега</span>
              )}
            </span>
          )}
          <Button
            variant={activeOther ? 'secondary' : 'primary'}
            onClick={onStart}
            disabled={take.isPending}
          >
            <Icon name={activeOther ? 'reply' : 'play'} size={16} />
            {label}
          </Button>
        </div>
      )}

      <Dialog
        open={confirm === 'takeover'}
        onClose={() => setConfirm(null)}
        title="Забрать задачу себе?"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              Отмена
            </Button>
            <Button onClick={afterTakeover}>Забрать</Button>
          </>
        }
      >
        {activeOther
          ? `Задачу ведёт ${activeEmployee?.name ?? 'коллега'} — этот таймер остановится, исполнителем станете вы.`
          : `Задача назначена на ${byId.get(assigneeOther ?? '')?.name ?? 'другого сотрудника'}. Взяв её, вы станете исполнителем.`}
      </Dialog>

      <Dialog
        open={confirm === 'busy'}
        onClose={() => setConfirm(null)}
        title={others.length === 1 ? 'Уже в работе одна задача' : `Уже в работе: ${others.length}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              Отмена
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setConfirm(null)
                start(true)
              }}
            >
              Остановить прежние
            </Button>
            <Button
              onClick={() => {
                setConfirm(null)
                start(false)
              }}
            >
              Вести обе
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-2">
          <ul className="flex flex-col gap-1 text-sm text-text-secondary">
            {others.slice(0, 3).map((r) => (
              <li key={r.taskId} className="truncate">
                · {r.taskTitle}
              </li>
            ))}
            {others.length > 3 && <li className="text-text-tertiary">и ещё {others.length - 3}</li>}
          </ul>
          <p className="text-sm text-text-tertiary">
            «Остановить прежние» вернёт их в колонки, из которых их взяли. «Вести обе» оставит время
            идти сразу по нескольким задачам.
          </p>
        </div>
      </Dialog>
    </>
  )
}
